/**
 * Hook for multi-document signal creation with realtime progress tracking.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { createDraftFromFiles, finalizeDraftSignal } from '../utils/api';
import { useRealtimeSubscriptions } from './useRealtimeUpdates';
import { useAuth } from '../contexts/AuthContext';
import type {
  MergedDraftSignalResponse,
  EditableSignal,
  FileProcessingStatus,
} from '../types/multi-document';
import { RealtimeMessage } from '../services/RealtimeClient';

interface UseMultiDocumentSignalOptions {
  onSuccess?: (signalId: string) => void;
  onError?: (error: Error) => void;
}

export const useMultiDocumentSignal = (options: UseMultiDocumentSignalOptions = {}) => {
  const { onSuccess, onError } = options;
  const { user } = useAuth();
  
  // State for file processing
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [completedFiles, setCompletedFiles] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);
  const [hasErrors, setHasErrors] = useState(false);
  const [fileProcessingStatus, setFileProcessingStatus] = useState<Map<string, FileProcessingStatus>>(new Map());
  const [signalId, setSignalId] = useState<string | null>(null);
  
  // Realtime subscriptions
  const { subscribe } = useRealtimeSubscriptions();
  
  // Track processed messages to prevent duplicates (key: `${type}-${signalId}-${timestamp}`)
  const processedMessagesRef = useRef<Set<string>>(new Set());
  
  // Mutation for creating draft from files
  const createDraftMutation = useMutation<MergedDraftSignalResponse, Error, { files: File[]; datasetId?: string }>({
    mutationFn: async ({ files, datasetId }) => {
      setIsProcessing(true);
      setTotalFiles(files.length);
      setCompletedFiles(0);
      setProcessingProgress(0);
      setHasErrors(false);
      setFileProcessingStatus(new Map());
      processedMessagesRef.current.clear(); // Clear processed messages for new upload
      
      // Initialize file statuses
      const initialStatuses = new Map<string, FileProcessingStatus>();
      files.forEach((file) => {
        initialStatuses.set(file.name, {
          filename: file.name,
          status: 'pending',
        });
      });
      setFileProcessingStatus(initialStatuses);
      
      const response = await createDraftFromFiles(files, datasetId);
      
      // Set signal ID for realtime subscription
      if (response.signal_id) {
        setSignalId(response.signal_id);
      }
      
      return response;
    },
    onSuccess: (data) => {
      if (data.success && data.signal_id) {
        // Processing will continue via realtime updates
        // Don't set isProcessing to false here - wait for completion message
      } else {
        setIsProcessing(false);
        if (onError) {
          onError(new Error(data.message || 'Failed to create draft signal'));
        }
      }
    },
    onError: (error) => {
      setIsProcessing(false);
      if (onError) {
        onError(error);
      }
    },
  });
  
  // Mutation for finalizing signal
  const finalizeMutation = useMutation<void, Error, string>({
    mutationFn: async (signalId: string) => {
      await finalizeDraftSignal(signalId);
    },
    onSuccess: () => {
      if (signalId && onSuccess) {
        onSuccess(signalId);
      }
    },
    onError: (error) => {
      if (onError) {
        onError(error);
      }
    },
  });
  
  // Subscribe to org channel immediately when processing starts (before we have signalId)
  // This ensures we catch early messages sent right after signal creation
  useEffect(() => {
    if (!isProcessing || !user?.organization_id) return;
    
    const handleMessage = (message: RealtimeMessage) => {
      // Get signal_id from message (may be in data or top-level)
      const messageSignalId = (message.data as any)?.signal_id || (message as any).signal_id;
      
      // If we have a signalId, only process messages for our signal
      // If we don't have signalId yet, process all document.* messages (they'll be filtered later)
      if (signalId && messageSignalId !== signalId) {
        return; // Not for our signal
      }
      
      // Create unique message key to prevent duplicates
      const messageKey = `${message.type}-${messageSignalId || 'pending'}-${message.timestamp || Date.now()}`;
      if (processedMessagesRef.current.has(messageKey)) {
        return; // Already processed this message
      }
      processedMessagesRef.current.add(messageKey);
      
      if (message.type === 'document.processing') {
        // Initial processing notification - update signalId if we don't have it yet
        if (!signalId && messageSignalId) {
          setSignalId(messageSignalId);
        }
      } else if (message.type === 'document.progress') {
        const { file_index, total_files, filename, status } = message.data as any;
        
        setFileProcessingStatus((prev) => {
          const next = new Map(prev);
          const currentStatus = next.get(filename) || { filename, status: 'pending' };
          next.set(filename, {
            ...currentStatus,
            status: status || 'processing',
            progress: (file_index / total_files) * 100,
          });
          return next;
        });
        
        setProcessingProgress((file_index / total_files) * 100);
        setCompletedFiles(file_index);
      } else if (message.type === 'document.completed') {
        // Mark all files as completed
        setFileProcessingStatus((prev) => {
          const next = new Map(prev);
          // Update all existing files to completed status
          prev.forEach((status, filename) => {
            if (status.status !== 'error') {
              next.set(filename, {
                ...status,
                status: 'completed',
                progress: 100,
              });
            }
          });
          return next;
        });
        
        setIsProcessing(false);
        setProcessingProgress(100);
        setCompletedFiles(totalFiles);
      } else if (message.type === 'document.failed') {
        const { filename, error } = message.data as any;
        
        setFileProcessingStatus((prev) => {
          const next = new Map(prev);
          next.set(filename, {
            filename,
            status: 'error',
            error: error || 'Processing failed',
          });
          return next;
        });
        
        setHasErrors(true);
      }
    };
    
    // Subscribe to org channel (catches all messages, including early ones)
    const unsubscribeOrg = subscribe(
      'org',
      user.organization_id,
      handleMessage,
      { messageTypes: ['document.processing', 'document.progress', 'document.completed', 'document.failed'] }
    );
    
    return unsubscribeOrg;
  }, [isProcessing, signalId, subscribe, totalFiles, user?.organization_id]);
  
  const processFiles = useCallback(
    async (files: File[], datasetId?: string) => {
      return createDraftMutation.mutateAsync({ files, datasetId });
    },
    [createDraftMutation]
  );
  
  const finalize = useCallback(
    async (id: string) => {
      return finalizeMutation.mutateAsync(id);
    },
    [finalizeMutation]
  );
  
  return {
    processFiles,
    finalize,
    isProcessing,
    processingProgress,
    completedFiles,
    totalFiles,
    hasErrors,
    fileProcessingStatus,
    signalId: createDraftMutation.data?.signal_id || signalId,
    extractedData: createDraftMutation.data?.extracted_data || null,
    warnings: createDraftMutation.data?.warnings || [],
    errors: createDraftMutation.data?.errors || [],
    isSaving: finalizeMutation.isPending,
    isLoading: createDraftMutation.isPending,
    error: createDraftMutation.error,
    createDraftMutation, // Expose mutation for accessing full response
  };
};
